// inventoryService.js
// Handles the daily QB CSV upload -> resets stock counts + imports barcodes.
// (Order-time stock deduction lives in orderService.submitOrder, inside the
// same transaction that creates the order.)

import { db } from "../config/firebase";
import {
  doc, collection, addDoc, serverTimestamp, writeBatch, getDocs
} from "firebase/firestore";
import Papa from "papaparse";
import { invalidateProductCache } from "./dataCache";

/**
 * Number cell from the QB export -> number. QuickBooks writes anything from
 * 1,000 up WITH a thousands comma (the cell comes through quoted, e.g.
 * "1,288.50"); parseFloat stops at the comma and read 10001's 1,288.50 as 1
 * on 2026-10-27, so the separators are stripped first. Also accepts the
 * accounting negative "(12.00)". Returns NaN for a blank or non-numeric cell.
 */
export function parseQbNumber(raw) {
  const s = String(raw ?? "").trim().replace(/,/g, "");
  if (s === "") return NaN;
  const accountingNegative = /^\(.*\)$/.test(s);
  const n = parseFloat(accountingNegative ? s.slice(1, -1) : s);
  return accountingNegative ? -n : n;
}

/**
 * Parses the QuickBooks inventory export and overwrites stock + core info per SKU.
 * Matches the actual QB "Item List" CSV export format (0-indexed columns):
 *   blank, blank, Item, blank, Purchase Description, blank, Price, blank,
 *   Quantity On Hand, blank, Barcode
 * (Same format used for the original one-time product import; the Barcode
 * column was added to the export 2026-07-09.)
 * Row 1 is a header row and is skipped.
 *
 * Any SKU not already in Firestore is created fresh with full details (name, brand,
 * price, stock) so new items show up in the catalog immediately, not broken/incomplete.
 *
 * Barcodes: stored as STRINGS, and written ONLY when the CSV has one — a blank
 * barcode cell never erases an existing manual mapping made on the Barcode
 * Mapping page. When both exist, the QB value wins (QB is the source of truth).
 * Existing photos are untouched (not included in this write).
 */
export async function uploadInventorySnapshot(file, adminUid) {
  const text = await file.text();
  const parsed = Papa.parse(text, { header: false, skipEmptyLines: true });

  // Guard: only accept the QB Item List export. Uploading the wrong CSV here
  // (it happened with a customer list) creates garbage product records whose
  // doc IDs are whatever sits in column 2 — reject anything else loudly.
  const header = parsed.data[0] || [];
  if ((header[2] || "").trim() !== "Item" || (header[8] || "").trim() !== "Quantity On Hand") {
    throw new Error(
      "This doesn't look like the QuickBooks Item List export — nothing was uploaded. " +
      "Expected 'Item' in column 3 and 'Quantity On Hand' in column 9 of the header row. " +
      "Export via Reports → Item Listing, saved as CSV UTF-8, and try again."
    );
  }

  const rows = parsed.data.slice(1); // skip header row
  const batchSize = 450; // stay under Firestore's 500-write batch limit
  let itemsUpdated = 0;
  let newItemsCreated = 0;
  let barcodesImported = 0;

  const validRows = [];
  for (const row of rows) {
    if (row.length < 9) continue;
    const sku = (row[2] || "").trim();
    const desc = (row[4] || "").trim();
    const priceRaw = (row[6] || "").trim();
    const qtyRaw = (row[8] || "").trim();
    // Older exports have no col 10 -> "". Strip label-font framing asterisks
    // (QB data entered as "*8801047496083*") — they'd never match a scan.
    const barcode = (row[10] || "").trim().replace(/^\*+|\*+$/g, "").trim();
    const qty = parseQbNumber(qtyRaw);
    const price = parseQbNumber(priceRaw);
    if (!sku || isNaN(qty)) continue;

    const brand = desc.includes(")") ? desc.split(")")[0].trim() : "";
    validRows.push({ sku, name: desc, brand, price: isNaN(price) ? 0 : price, qty, barcode });
  }

  // Check which SKUs are brand new (for reporting back to the admin), and
  // remember which products were manually forced into the catalog — the
  // upload must NOT deactivate those even at zero stock (Brian, 2026-07-14).
  const existingSnap = await getDocs(collection(db, "products"));
  const existingSkus = new Set(existingSnap.docs.map(d => d.id));
  const forceActive = new Set(existingSnap.docs.filter(d => d.data().forceActive).map(d => d.id));
  // Discontinued marks (bulk sheet on admin Products) auto-clear when QB
  // shows stock again — a revived product must not stay hidden by accident.
  const discontinued = new Set(existingSnap.docs.filter(d => d.data().discontinued).map(d => d.id));

  for (let i = 0; i < validRows.length; i += batchSize) {
    const batch = writeBatch(db);
    const chunk = validRows.slice(i, i + batchSize);

    chunk.forEach(r => {
      if (!existingSkus.has(r.sku)) newItemsCreated++;
      const productRef = doc(db, "products", r.sku);
      const data = {
        sku: r.sku,
        name: r.name,
        brand: r.brand,
        basePrice: r.price,
        stockQty: Math.max(0, r.qty),
        active: r.qty > 0 || forceActive.has(r.sku),
        lastInventorySync: serverTimestamp()
      };
      if (r.qty > 0 && discontinued.has(r.sku)) {
        data.discontinued = false; // back in stock → back in the catalog
      }
      if (r.barcode) {
        data.barcode = String(r.barcode);
        data.barcodeSource = "qb"; // QB knows this barcode -> not in the "export for QB" list
        barcodesImported++;
      }
      batch.set(productRef, data, { merge: true });
      itemsUpdated++;
    });

    await batch.commit();
  }

  await addDoc(collection(db, "inventorySnapshots"), {
    uploadedBy: adminUid,
    uploadedAt: serverTimestamp(),
    fileName: file.name,
    itemsUpdated,
    newItemsCreated,
    barcodesImported,
    status: "processed"
  });

  invalidateProductCache(); // whole catalog just changed
  return { itemsUpdated, newItemsCreated, barcodesImported };
}
